/**
 * Product Main Gallery 360° Video Controller
 * Functional approach for controlling video playback based on mouse cursor movement
 * Global window mouse tracking when section is in view using intersection observer
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    videoSelector: '.flotilla-360-video',
    containerSelector: '.flotilla-360-video-container',
    defaultSensitivity: 1.0,
    videoStartPosition: 0.1, // Start at 50% of video duration
    // Smooth seeking settings
    leftBound: 0.1, // 10% of window width (like CodePen example)
    rightBound: 0.9, // 90% of window width (like CodePen example)
    updateInterval: 75, // Update interval in ms (same as CodePen example)
    easingDuration: 0.6, // Easing duration for smooth transitions
    positionThreshold: 0.01, // Minimum difference to update position (prevents jitter)
    // Intersection observer settings
    intersectionThreshold: 0.5, // Section must be 50% visible to activate
    rootMargin: '0px' // No margin around viewport
  };

  // State management
  let videoControllers = [];

  /**
   * Initialize the 360 video controller
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupVideoControllers);
    } else {
      setupVideoControllers();
    }
  }

  /**
   * Set up video controllers for all 360 video containers
   */
  function setupVideoControllers() {
    const containers = document.querySelectorAll(CONFIG.containerSelector);

    containers.forEach((container, index) => {
      const video = container.querySelector(CONFIG.videoSelector);
      const sensitivity = parseFloat(container.dataset.sensitivity) || CONFIG.defaultSensitivity;

      if (video) {
        const controller = createVideoController(container, video, sensitivity);
        videoControllers.push(controller);
      }
    });
  }

  /**
   * Map function (from CodePen example)
   */
  function map(num, in_min, in_max, out_min, out_max) {
    return (num - in_min) * (out_max - out_min) / (in_max - in_min) + out_min;
  }

  /**
   * Create a video controller for a specific video element
   */
  function createVideoController(container, video, sensitivity) {
    // Check if autoplay mode - if so, exit early and let video play normally
    const overviewMode = container.dataset.overviewMode;
    if (overviewMode === 'autoplay') {
      return {
        container,
        video,
        sensitivity,
        destroy: () => {} // No-op destroy function
      };
    }

    let lastMouseX = 0;
    let lastMouseY = 0;
    let isInView = false;
    let observer = null;
    let updateInterval = null;

    // State for smooth seeking (like CodePen approach)
    const timeState = {
      target: video.duration * CONFIG.videoStartPosition, // Target time for easing
      current: video.duration * CONFIG.videoStartPosition // Current time for smooth transitions
    };

    /**
     * Calculate target time based on mouse position
     */
    function calculateTargetTime(mouseX) {
      const windowWidth = window.innerWidth;
      const leftBound = windowWidth * CONFIG.leftBound;
      const rightBound = windowWidth * CONFIG.rightBound;

      // Clamp mouse position to bounds
      const clampedX = Math.min(rightBound, Math.max(leftBound, mouseX));

      // Get video duration (with fallback)
      const videoDuration = video.duration || 1;

      // Map mouse position to video duration (like CodePen)
      const targetTime = Math.min(
        videoDuration - 0.5, // Leave 0.5s buffer at end
        Math.max(0, map(clampedX, leftBound, rightBound, 0, videoDuration))
      );

      return targetTime;
    }

    /**
     * Smooth easing update function
     */
    function updateVideoTime() {
      if (!isInView) return;

      // Calculate difference between target and current
      const diff = timeState.target - timeState.current;

      // Only update if difference is significant (prevents jitter)
      if (Math.abs(diff) < CONFIG.positionThreshold) {
        timeState.current = timeState.target;
      } else {
        // Smoothly interpolate from current to target time
        const easing = CONFIG.easingDuration;
        timeState.current += diff * easing;
      }

      // Ensure time stays within bounds
      const videoDuration = video.duration || 1;
      timeState.current = Math.max(0, Math.min(videoDuration, timeState.current));

      // Update video currentTime only if there's a significant difference
      const timeDiff = Math.abs(video.currentTime - timeState.current);
      if (timeDiff > CONFIG.positionThreshold) {
        video.currentTime = timeState.current;
      }
    }

    /**
     * Handle mouse move events
     */
    const handleMouseMove = (e) => {
      lastMouseX = e.clientX;
      lastMouseY = e.clientY;

      if (isInView) {
        // Calculate target time based on mouse position
        timeState.target = calculateTargetTime(lastMouseX);
      }
    };


    /**
     * Handle video end events
     */
    const handleVideoEnd = () => {
      if (isInView) {
        // Reset to beginning when video ends
        video.currentTime = 0;
      }
    };

    /**
     * Handle intersection observer changes
     */
    const handleIntersection = (entries) => {
      entries.forEach(entry => {
        isInView = entry.isIntersecting;

        if (isInView) {
          // Section is in view - enable smooth seeking
          if (!updateInterval && video.duration) {
            updateInterval = setInterval(updateVideoTime, CONFIG.updateInterval);
          }
        } else {
          // Section is out of view - stop seeking and reset video
          if (updateInterval) {
            clearInterval(updateInterval);
            updateInterval = null;
          }
          // Reset video to paused state (no playback)
          video.pause();
          const videoDuration = video.duration || 1;
          const startPosition = videoDuration * CONFIG.videoStartPosition;
          video.currentTime = startPosition; // Reset to middle position
          timeState.target = startPosition;
          timeState.current = startPosition;
        }
      });
    };

    /**
     * Handle video load events
     */
    const handleVideoLoad = () => {
      // Ensure video starts at a reasonable position for smooth interaction
      const videoDuration = video.duration || 1;
      if (videoDuration > 1) {
        // Set initial time to middle of video for better 360° experience
        const startPosition = videoDuration * CONFIG.videoStartPosition;
        video.currentTime = startPosition;
        timeState.target = startPosition;
        timeState.current = startPosition;
      }
      // Ensure video is paused (no playback)
      video.pause();

      // Start the update interval if video is ready and section is in view
      if (isInView && video.duration && !updateInterval) {
        updateInterval = setInterval(updateVideoTime, CONFIG.updateInterval);
      }
    };


    /**
     * Clean up event listeners and observers
     */
    const destroy = () => {
      // Disconnect intersection observer
      if (observer) {
        observer.disconnect();
      }

      // Remove document event listener
      document.removeEventListener('mousemove', handleMouseMove);

      // Clear update interval
      if (updateInterval) {
        clearInterval(updateInterval);
      }

      video.removeEventListener('loadeddata', handleVideoLoad);
      video.removeEventListener('ended', handleVideoEnd);
    };

    // Set up intersection observer
    observer = new IntersectionObserver(handleIntersection, {
      threshold: CONFIG.intersectionThreshold,
      rootMargin: CONFIG.rootMargin
    });

    observer.observe(container);

    // Add document event listener for global mouse tracking
    document.addEventListener('mousemove', handleMouseMove);

    video.addEventListener('loadeddata', handleVideoLoad);
    video.addEventListener('ended', handleVideoEnd);

    return {
      container,
      video,
      sensitivity,
      destroy
    };
  }

  /**
   * Update sensitivity for all video controllers
   */
  function updateSensitivity(newSensitivity) {
    videoControllers.forEach(controller => {
      controller.container.dataset.sensitivity = newSensitivity;
    });
  }

  /**
   * Refresh all video controllers (useful if new videos are added dynamically)
   */
  function refresh() {
    // Clean up existing controllers
    videoControllers.forEach(controller => controller.destroy());
    videoControllers = [];

    // Set up new controllers
    setupVideoControllers();
  }

  /**
   * Get current video controllers
   */
  function getControllers() {
    return [...videoControllers];
  }

  // Public API
  window.ProductMainGallery = {
    init,
    refresh,
    updateSensitivity,
    getControllers
  };

  // Auto-initialize
  init();

})();
